import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Check } from "lucide-react";
import { useTranslation } from "@/i18n/LanguageContext";
import { SUPPORTED_LANGUAGES } from "@/i18n/translations";

/**
 * Monochrome language switcher — pill trigger + glass dropdown rendered in a
 * portal with fixed positioning so it never gets clipped by the header pill.
 */
export default function LangSwitcher({ className = "" }) {
  const { lang, setLang } = useTranslation();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const place = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const width = 152;
    setPos({ top: r.bottom + 8, left: Math.min(Math.max(8, r.right - width), window.innerWidth - width - 8), width });
  };

  const toggle = () => {
    if (!open) place();
    setOpen((v) => !v);
  };

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (btnRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("mousedown", close);
    window.addEventListener("keydown", onKey);
    window.addEventListener("scroll", () => setOpen(false), { once: true, passive: true });
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("mousedown", close);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", place);
    };
  }, [open]);

  const current = SUPPORTED_LANGUAGES.find((l) => l.code === lang) || SUPPORTED_LANGUAGES[1];

  return (
    <div className={`relative inline-flex ${className}`} data-testid="lang-switcher">
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Language"
        data-testid="lang-switcher-trigger"
        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-2 text-[10px] tracking-[0.25em] uppercase transition-colors ${
          open
            ? "border-white/45 text-white bg-white/[0.06]"
            : "border-white/[0.12] text-neutral-300 hover:text-white hover:border-white/35"
        }`}
      >
        {current.short}
        <ChevronDown size={11} strokeWidth={1.6} className={`opacity-60 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && pos && createPortal(
        <div
          ref={menuRef}
          role="listbox"
          data-testid="lang-switcher-menu"
          style={{ top: pos.top, left: pos.left, width: pos.width }}
          className="fixed z-[200] rounded-2xl border border-white/[0.12] bg-[#0A0A0A]/95 backdrop-blur-xl p-1.5 shadow-[0_30px_70px_-20px_rgba(0,0,0,0.95)]"
        >
          {SUPPORTED_LANGUAGES.map((l) => {
            const active = l.code === lang;
            return (
              <button
                key={l.code}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => { setLang(l.code); setOpen(false); }}
                data-testid={`lang-option-${l.code}`}
                className={`w-full flex items-center justify-between gap-3 rounded-xl px-3 py-2 text-left transition-colors ${
                  active ? "bg-white/[0.08] text-white" : "text-neutral-400 hover:text-white hover:bg-white/[0.04]"
                }`}
              >
                <span className="text-[12px] font-light">{l.label}</span>
                {active && <Check size={12} className="text-white" />}
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
}
